import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { Gift } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { OptimizedImage } from '@/shared/ui/optimized-image'
import {
  formatSetPrice,
  getSetEffectivePrice,
  getSetSavings,
  getSetScenarioLabel,
  resolveSetImage,
  setComponentSummary,
} from '../lib/set-logic'
import { cn } from '@/shared/lib/utils'
import type { ProductSet } from '../model/types'

export interface SetCardProps {
  set: ProductSet
  onClick?: () => void
  className?: string
}

const SET_FALLBACK = '/placeholder-product.svg'

/**
 * Карточка подарочного набора для каталога миниаппа.
 * Показывает сценарий, состав, цену и экономию комплекта.
 */
export function SetCard({ set, onClick, className }: SetCardProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  const image = resolveSetImage(set, SET_FALLBACK)
  const price = getSetEffectivePrice(set)
  const savings = getSetSavings(set)
  const scenario = getSetScenarioLabel(set)
  const notPurchasable = !set.isPurchasable || !set.isInStock

  const handleClick = () => {
    hapticFeedback.selectionChanged()
    onClick?.()
    navigate({ to: '/product/$productId', params: { productId: set.id } })
  }

  return (
    <motion.div
      whileTap={{ scale: 0.97 }}
      onClick={handleClick}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[16px] bg-white dark:bg-dark-card shadow-sm border border-gray-100 dark:border-dark-border cursor-pointer',
        notPurchasable && 'opacity-60',
        className,
      )}
    >
      {/* Изображение */}
      <div className="relative aspect-[4/3] bg-paper dark:bg-dark-elevated overflow-hidden">
        <OptimizedImage src={image} alt={set.name} preset="card" className="w-full h-full object-cover" />
        {/* Бейджи */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          <span className="inline-flex items-center gap-1 rounded-md bg-primary/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            <Gift size={10} />
            {scenario}
          </span>
          {set.isFeatured && (
            <span className="rounded-md bg-gold px-1.5 py-0.5 text-[10px] font-bold text-ink">хит</span>
          )}
          {set.isNew && (
            <span className="rounded-md bg-ink px-1.5 py-0.5 text-[10px] font-bold text-white">новинка</span>
          )}
        </div>
        {notPurchasable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-gray-700">
              нет в наличии
            </span>
          </div>
        )}
      </div>

      {/* Контент */}
      <div className="flex flex-1 flex-col p-3">
        <h3 className="font-display text-[15px] leading-tight text-ink dark:text-white line-clamp-2">
          {set.name}
        </h3>
        {set.shortDescription && (
          <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400 line-clamp-1">
            {set.shortDescription}
          </p>
        )}

        {/* Состав */}
        <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">{setComponentSummary(set)}</p>

        {/* Цена + экономия */}
        <div className="mt-auto pt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-[17px] font-bold text-ink dark:text-white">{formatSetPrice(price)}</span>
            {savings > 0 && (
              <span className="text-[11px] font-medium text-jade">
                −{formatSetPrice(savings)}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
